// 出撃時の最終性能（BattleLoadout）と戦力。推奨戦力は「ステージ s での想定育成」の戦力から求める。
// 戦闘側は BattleLoadout だけを見る。キャラの固有パッシブ（data/chars.ts の passive）は戦闘側で扱い、ここでは足さない。
import { CHARS } from '../data/chars';
import type { BattleLoadout, BuildingType, CharId } from '../types';
import { CHAR_IDS } from '../types';
import { CONFIG, configVersion } from './config';
import { EQUIP_LINES } from '../data/equip-lines';
import { AFFIX_POOL, EQUIPMENT as EQUIP_DEFS, lineDefId, equippedItems, itemStats, itemTraitEffects, itemTraitFlags, setBonuses } from './equipment';
import { addMod, DISCRETE_KEYS, statParts, type Mods, type ModKey } from './mods';
import { equipOmamori, omamoriKind, omamoriMods, omamoriOwner, stageToQ } from './omamori';
import { chapterOf, isChapterBoss, indexInChapter, newProfile, type AffixId, type Profile } from './profile';
import { skillLines } from './skill-lines';
import { companionEffects, SHIKIGAMI, SHIKIGAMI_IDS, shikigamiMods, stageOf } from './shikigami';
import { applyAutoBuild, applyStyleSkills, autoBuild, BUILD_ORDER, BUILD_START, OUGI_SKILL_IDS, SKILLS, selectedJutsu, skillMods } from './skills';
import { defaultJutsu, JUTSU_BY_ID } from '../data/jutsu';
import { ITEM_BASE_COUNT, ITEM_SLOTS, itemCounts, setKit, unlockedItems } from './items';
import { styleOf } from './styles';
import { STAGE_TUNE } from '../battle/stage-tune';

export const POTION_BASE = { count: 2, heal: 0.35 };
export const BUILD_BASE = { maxCount: 2, perTurn: 1, range: 1 };
/** 戦闘開始時の陣材（battle の CFG.material.start と同じ） */
export const MATERIAL_BASE = 4;

/** 全要素の効果の合算（スキル・装備の固有効果と追加効果・お守り・式神・お供） */
export function collectMods(profile: Profile, charId: CharId): { mods: Mods; flags: string[] } {
  const { mods, flags } = skillMods(profile, charId);
  // 固定値の追加効果（陣材収入・復活）は装備全体で1つ分まで（3部位で重ねて強くなりすぎないように）
  const fixedSeen = new Set<string>();
  const jutsu = selectedJutsu(profile, charId);
  for (const item of equippedItems(profile, charId)) {
    // 系統の固有効果。相性の術を持っていくと synergyMult 倍。戦い方が変わる固有（フラグ）が他と重なった分は最後に数値へ換える
    const syn = EQUIP_DEFS[item.defId]?.jutsu === jutsu ? CONFIG.equipment.synergyMult : 1;
    for (const e of itemTraitEffects(item)) addMod(mods, e.key, DISCRETE_KEYS.has(e.key) ? e.value : e.value * syn);
    flags.push(...itemTraitFlags(item));
    for (const a of item.affixes ?? []) {
      const d = AFFIX_POOL[a.id];
      if (!d) continue;
      if (d.fixed) {
        if (fixedSeen.has(a.id)) continue;
        fixedSeen.add(a.id);
      }
      addMod(mods, d.effect, a.value * (d.sign ?? 1));
      // デメリット付きの銘の固定のデメリット・戦闘のフラグ（反撃・再生）
      for (const x of d.extra ?? []) addMod(mods, x.key, x.value);
      if (d.flags) flags.push(...d.flags);
    }
  }
  // 業物（セット）：同じシリーズを2部位・3部位そろえた効果
  for (const sb of setBonuses(equippedItems(profile, charId))) {
    for (const e of sb.effects) addMod(mods, e.key, e.value);
    flags.push(...sb.flags);
  }
  // お守り（主効果＋副効果。副効果は効果ごとの合計に上限。妖力の溜まりやすさは comp:surge の点に足す）
  const om = omamoriMods(profile, charId);
  for (const k of Object.keys(om.mods) as ModKey[]) addMod(mods, k, om.mods[k]!);
  flags.push(...om.flags);
  const sm = shikigamiMods(profile, charId);
  for (const k of Object.keys(sm) as ModKey[]) addMod(mods, k, sm[k]!);
  const comp = companionEffects(profile, charId);
  for (const k of Object.keys(comp.mods) as ModKey[]) addMod(mods, k, comp.mods[k]!);
  let compFlags = comp.flags;
  if (om.surge > 0) {
    const cur = compFlags.find((f) => f.startsWith('comp:surge='));
    const v = (cur ? Number(cur.split('=')[1]) || 0 : 0) + om.surge;
    compFlags = [...compFlags.filter((f) => f !== cur), `comp:surge=${v}`];
  }
  flags.push(...compFlags);
  // 同じ戦い方の効果が重なった分（2つ目以降）は、別の数値の効果に換える（戦闘はフラグを持っているかだけで見るため）
  const count = new Map<string, number>();
  for (const f of flags) count.set(f, (count.get(f) ?? 0) + 1);
  for (const [f, n] of count) for (const e of CONFIG.dupFlag[f] ?? []) if (n > 1) addMod(mods, e.key, e.value * (n - 1));
  return { mods, flags: [...new Set(flags)].sort() };
}


/**
 * 能力値：元の値は四捨五入、%の上乗せは出どころ（スキル1つ・銘1つ・お守り・式神など）ごとに切り上げて足す（マイナスは引く分を切り上げ）。
 * 整数の能力値なので、防御20前後に+3%のような小さな上乗せが四捨五入で消えて効かなかった（2026-10-23）。上乗せ1つにつき必ず±1以上
 */
const statWith = (raw: number, parts: readonly number[]) =>
  Math.round(raw) + parts.reduce((a, pct) => a + Math.sign(pct) * Math.ceil(Math.abs(raw * pct) - 1e-9), 0);
const r3 = (x: number) => Math.round(x * 1000) / 1000;

/** 能力が上がる主Lvの刻み */
export const STAT_STEP = 5;
/** 能力の計算に使う主Lv（STAT_STEP ごとにまとめて上がる） */
export const statLevel = (L: number) => Math.max(1, Math.min(CONFIG.level.max, STAT_STEP * Math.floor(L / STAT_STEP)));

export function buildLoadout(profile: Profile, charId: CharId): BattleLoadout {
  const ch = CHARS[charId];
  const b = ch.base;
  const L = profile.level;
  // 能力は主Lv5つごとにまとめて上がる（Lv5・10・15…で5つぶん。Lv1〜4はLv1の能力）
  const Ls = statLevel(L);
  // 系統（skill-lines）のキャラは、主Lvの伸びを半分にし、残りは系統の Lv の能力で（ビルドに合わせて伸ばす）
  const grow = skillLines(charId) ? CONFIG.skills.lineGrow : 1;
  const lv = 1 + CONFIG.level.statPerLevel * grow * (Ls - 1);
  const agiLv = 1 + CONFIG.level.agiPerLevel * (Ls - 1);
  const { mods: m, flags } = collectMods(profile, charId);
  const g = (k: ModKey) => m[k] ?? 0;
  const eq = { hp: 0, atk: 0, def: 0, agi: 0 };
  for (const item of equippedItems(profile, charId)) {
    const s = itemStats(item);
    eq.hp += s.hp;
    eq.atk += s.atk;
    eq.def += s.def;
    eq.agi += s.agi;
  }
  const items = itemCounts(profile, g('potionCount'), charId);
  // 建物：櫓・竹柵・幟は最初から。ほかは建物図面を Lv1 にすると建てられる（スキルでは覚えない。スキルは建物の強化）
  const unlocked = BUILD_ORDER.filter((t, i) => i < BUILD_START || (profile.blueprints[t] ?? 0) >= 1) as BuildingType[];
  const levels: Partial<Record<BuildingType, number>> = {};
  for (const t of unlocked) levels[t] = profile.blueprints[t] ?? 0;
  return {
    charId,
    level: L,
    stats: {
      hp: statWith(b.hp * lv + eq.hp, statParts(m, 'hp')),
      atk: statWith(b.atk * lv + eq.atk, statParts(m, 'atk')),
      def: statWith(b.def * lv + eq.def, statParts(m, 'def')),
      agi: statWith(b.agi * agiLv + eq.agi + g('agiFlat'), statParts(m, 'agi')),
      move: b.move + Math.round(g('move')),
      range: b.range + Math.round(g('range')),
    },
    skill: { power: r3(1 + g('skillPower')), cdDelta: Math.max(-2, Math.round(g('cd'))), splashBonus: r3(g('splash')) },
    build: {
      unlocked,
      levels,
      // 城崩し（buildCap1）：建てられる建物は1つまで
      maxCount: flags.includes('buildCap1') ? 1 : BUILD_BASE.maxCount + Math.round(g('maxCount')),
      perTurn: Math.min(2, BUILD_BASE.perTurn + Math.round(g('perTurn'))),
      costMult: r3(Math.max(0.5, 1 + g('buildCost'))),
      costFlat: Math.max(0, -Math.round(g('buildCostFlat'))),
      hpMult: r3(1 + g('buildHp')),
      powerMult: r3(1 + g('buildPower')),
      range: BUILD_BASE.range + Math.round(g('buildRange')),
    },
    // start は開始時の陣材（絶対値）、incomeBonus は毎手番の陣材の加算（整数）
    materials: { start: MATERIAL_BASE + Math.round(g('matStart')), incomeBonus: Math.floor(g('matIncome') + 1e-9) },
    potions: { count: healCount(items), heal: r3(POTION_BASE.heal + g('potionHeal')) },
    items,
    diceMin: 1 + Math.min(1, Math.round(g('diceMin'))),
    ...(() => {
      const dice = Object.fromEntries(
        ([['even', 'diceEven'], ['odd', 'diceOdd'], ['high', 'diceHigh'], ['low', 'diceLow'], ['pair', 'dicePair'], ['role', 'diceRole'], ['roleMat', 'diceRoleMat'], ['skill', 'diceSkill'], ['lowAtk', 'diceLowAtk'], ['noRole', 'diceNoRole']] as const)
          .map(([k, m]) => [k, k === 'low' || k === 'roleMat' ? Math.round(g(m)) : r3(g(m))])
          .filter(([k, v]) => (v as number) > 0 || (['odd', 'lowAtk', 'noRole'].includes(k as string) && (v as number) < 0)), // 博徒の外れ（負）：半の被ダメ増・大目に届かない・役なしの与ダメ減
      );
      return Object.keys(dice).length ? { dice } : {};
    })(),
    respawnDelta: Math.max(-2, Math.round(g('respawn'))),
    territoryBonus: r3(g('territory')),
    mult: { core: r3(1 + g('core')), boss: r3(1 + g('boss')), neutral: r3(1 + g('neutral')), taken: r3(Math.max(0.5, 1 + g('taken'))) },
    lifesteal: r3(g('lifesteal')),
    // 妖力の奥義：持っていく術の奥義（jougi:<術>）を覚えていれば使える
    flags: flags.includes(`jougi:${selectedJutsu(profile, charId)}`) ? [...flags, 'surge:ougi'].sort() : flags,
    // 持っていく術（基本の術なら書かない＝古い setup と同じ）
    ...(() => {
      const j = selectedJutsu(profile, charId);
      return j !== defaultJutsu(charId).id ? { jutsu: j } : {};
    })(),
    companion: (() => {
      const c = companionEffects(profile, charId).info;
      return c ? { enemyId: c.enemyId, star: c.star, level: c.level, ability: c.ability } : null;
    })(),
    shikigami: shikigamiMods(profile, charId),
  };
}

/** 戦力の内訳（掛け算の因子）。power = round(10 * core * build * rules * char) */
export function powerFactors(lo: BattleLoadout) {
  const s = lo.stats;
  const base = CHARS[lo.charId].base;
  const hpE = (s.hp * (1 + s.def / 60)) / lo.mult.taken;
  const cdF = 3 / Math.max(1, 3 + lo.skill.cdDelta);
  const off = 1 + 0.3 * (lo.mult.core - 1) + 0.15 * (lo.mult.boss - 1) + 0.15 * (lo.mult.neutral - 1);
  const dmg = s.atk * (s.agi / 100) * (0.8 + 0.2 * lo.skill.power * (1 + 0.5 * lo.skill.splashBonus) * cdF) * off;
  const core = Math.sqrt(hpE * dmg);
  const bl = lo.build;
  const lvSum = bl.unlocked.reduce((a, t) => a + (bl.levels[t] ?? 0), 0);
  // 建築の重みは battle-sim の較正（_flagcal）で実戦の効き目に合わせた（以前は実戦より3〜10倍高く見積もっていた）
  const build =
    1 +
    CONFIG.power.buildScale *
      (
      0.006 * (bl.unlocked.length - BUILD_START) +
      0.03 * (bl.maxCount - BUILD_BASE.maxCount) +
      0.01 * (bl.perTurn - 1) +
      0.03 * (bl.hpMult - 1) +
      0.04 * (bl.powerMult - 1) +
      0.03 * (1 - bl.costMult) +
      // 陣材-1 は建物（2〜9、よく使うのは4前後）の約2割引
      0.008 * (bl.costFlat ?? 0) +
      0.01 * (bl.range - 1) +
      0.0005 * lvSum +
      0.003 * (lo.materials.start - MATERIAL_BASE) +
      0.01 * lo.materials.incomeBonus);
  const rules =
    1 +
    0.06 * (lo.diceMin - 1) -
    0.03 * lo.respawnDelta +
    lo.territoryBonus +
    CONFIG.power.lifesteal * lo.lifesteal +
    0.02 * itemExtra(lo) +
    0.05 * (lo.potions.heal - POTION_BASE.heal) +
    companionAdj(lo) +
    shikigamiAdj(lo) +
    diceAdj(lo);
  const w = CONFIG.power.kit;
  const char = (1 + w.range * (s.range - 1)) * (base.magic ? 1 + w.magic : 1) * (1 + w.move * s.move);
  return { core, build, rules: rules * flagFactor(lo.flags), char };
}

/**
 * 賽の目で効く効果（rules 2）の見積もり：出る確率（偶数・奇数 1/2、10以上 5/8、9以下 3/8、同じ目 4/9、役 5/36）× 効果。
 * 与ダメ・被ダメは戦力の平方根の中なので半分。術の賽は術の割合（約2割）× 平均の上がり幅（12.5%）
 */
function diceAdj(lo: BattleLoadout): number {
  const d = lo.dice;
  if (!d) return 0;
  // 外れ（博徒）：9以下の手番の与ダメ減（3/8）・役なしの手番の与ダメ減（31/36）も引く
  const dmg = 0.5 * (d.even ?? 0) + 0.625 * (d.high ?? 0) + (4 / 9) * (d.pair ?? 0) + (5 / 36) * (d.role ?? 0) + 0.2 * 0.125 * (d.skill ?? 0) + 0.375 * (d.lowAtk ?? 0) + (31 / 36) * (d.noRole ?? 0);
  // 実戦では攻撃しない手番・術の手番もあり、式の見積もりの約半分しか効かない（battle-sim の較正：博徒の振り方の戦力と勝率。CONFIG.power.diceScale）
  return CONFIG.power.diceScale * (0.5 * dmg + 0.5 * 0.5 * (d.odd ?? 0)) + 0.05 * 0.375 * (d.low ?? 0) + 0.004 * (d.roleMat ?? 0);
}

/** 式神の持っているだけの効果の、式の見積もりと実戦の差の補正（CONFIG.power.shikigamiAdj：効果の種類ごとに値あたり） */
function shikigamiAdj(lo: BattleLoadout): number {
  let f = 0;
  for (const [k, v] of Object.entries(lo.shikigami ?? {})) f += (CONFIG.power.shikigamiAdj[k] ?? 0) * (v ?? 0);
  return f;
}

/** 数値で渡すお供の能力（★3 の第二の能力を含む）の、式の見積もりと実戦の差の補正（CONFIG.power.companionAdj：効果の種類ごとに値あたり） */
function companionAdj(_lo: BattleLoadout): number {
  return 0; // 式神の加勢は数値の能力を持たない（フラグだけ）
}

/** 戦闘のフラグ（スキル・お守りの特殊効果）の戦力の倍率。重みは CONFIG.power */
export function flagFactor(flags: readonly string[]): number {
  const w = CONFIG.power;
  let f = 1;
  for (const flag of new Set(flags)) {
    if (w.flags[flag] !== undefined) f += w.flags[flag];
    else if (flag.startsWith('comp:')) {
      // お供の能力 comp:<キー>=<値>：重み × 値
      const [k, v] = flag.slice(5).split('=');
      f += (w.companion[k] ?? 0) * (Number(v) || 0);
    } else if (flag.startsWith('boon:')) {
      // 式神の定期の加護 boon:<種類>=<値>:<手番>:<最大>：重み × 値 × 最大の重ね
      const [kv, , max] = flag.slice(5).split(':');
      const [k, v] = kv.split('=');
      f += (w.boon[k] ?? 0) * (Number(v) || 0) * (Number(max) || 1);
    } else if (flag.startsWith('shiki:') && flag.includes(':uses=')) {
      continue; // 回数は効果の側で掛ける
    } else if (flag.startsWith('shiki:')) {
      // 式神の奥義 shiki:<条件>:<効果>=<値>：効果の重み × 値（power は 値-1）× 条件の倍率
      const [head, v] = flag.slice(6).split('=');
      const [trig, eff] = head.split(':');
      const n = Number(v) || 0;
      const uses = Number(flags.find((x) => x.startsWith(`shiki:${trig}:uses=`))?.split('=')[1]) || 1;
      f += (w.ougiEffect[eff] ?? 0) * (eff === 'power' ? Math.max(0, n - 1) : n) * (w.ougiTrigger[trig] ?? 0.5) * (1 + 0.6 * (uses - 1));
    } else if (flag.startsWith('surge:')) {
      const parts = flag.split(':');
      if (parts[1] === 'fast') f += w.surgeFast * Math.abs(Number(parts[2] ?? -1) || 0);
      else if (parts[1] === 'boost' && parts[3]) {
        const [st, v] = parts[3].split('=').map(Number);
        f += (w.surgeBoost[parts[2]]?.[st] ?? 0) * Math.max(0, (v || 1) - 1) * 2;
      } else if (/^on\d$/.test(parts[1] ?? '')) f += w.surgeOn[parts[2] ?? ''] ?? 0;
    }
  }
  return f;
}

export function loadoutPower(lo: BattleLoadout): number {
  // 能力値だけ（CONFIG.power.statOnly）：√(耐久 × 火力) × 10。耐久 = HP ×（1 + 防御/60）、火力 = 攻撃 × 速さ/100。スキル・建物・賽などの効果は入れない
  if (CONFIG.power.statOnly) {
    const s = lo.stats;
    return Math.round(10 * Math.sqrt(s.hp * (1 + s.def / 60) * s.atk * (s.agi / 100)));
  }
  const f = powerFactors(lo);
  return Math.round(10 * f.core * f.build * f.rules * f.char);
}

export const powerOf = (profile: Profile, charId: CharId) => loadoutPower(buildLoadout(profile, charId));
/** 選ぶとき（おすすめ装備・お守り・お供）の目安：効果（スキル・建物・賽・戦い方）の見積もりも入れた式（statOnly が false なら画面の戦力と同じ） */
export const ratePower = (lo: BattleLoadout) => {
  const f = powerFactors(lo);
  return Math.round(10 * f.core * f.build * f.rules * f.char);
};
export const rateOf = (profile: Profile, charId: CharId) => ratePower(buildLoadout(profile, charId));

// ---- 想定育成（推奨戦力・戦闘側の testLoadout の目安） ----

/** 折れ線 [[面, 値], …] の補間（範囲外は端の値） */
function polyline(points: [number, number][], x: number): number {
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i];
    if (x <= x1) {
      const [x0, y0] = points[i - 1];
      return y0 + ((y1 - y0) * (x - x0)) / Math.max(1e-9, x1 - x0);
    }
  }
  return points[points.length - 1][1];
}

export function standardLevel(stageId: number): number {
  const s = CONFIG.standard;
  // 想定主Lv の折れ線（ペース曲線：序盤と終盤は一気に、中盤は育成。BALANCE.md 17章）。空なら下の式
  if (s.levelPoints?.length) return Math.max(1, Math.min(CONFIG.level.max, Math.round(polyline(s.levelPoints, stageId))));
  // 序盤（〜earlyUntil）は緩い直線から本来の曲線へ徐々に寄せる
  const curve = s.levelA + s.levelB * Math.pow(stageId - 1, s.levelP);
  const early = 1 + s.earlySlope * (stageId - 1);
  const w = Math.max(0, Math.min(1, (stageId - s.scaleFrom) / (s.earlyUntil - s.scaleFrom)));
  const fit = Math.min(curve, (1 - w) * early + w * curve);
  return Math.max(1, Math.min(CONFIG.level.max, Math.round(fit)));
}

/** ステージ stageId を初めて遊ぶ時点の「標準的なプレイヤー」 */
export function standardProfile(stageId: number): Profile {
  const s = CONFIG.standard;
  // 想定の装備の段（ふつうは章。gearTierPoints があればその折れ線＝終盤は一気に進むので段が章に追いつかない）
  const ch = chapterOf(stageId);
  const tier = s.gearTierPoints?.length ? Math.max(1, Math.min(ch, Math.round(polyline(s.gearTierPoints, stageId)))) : ch;
  const p = newProfile(0);
  p.level = standardLevel(stageId);
  const enh = Math.max(0, Math.floor(s.enhA + s.enhB * (stageId - 1)));
  const lb = Math.min(CONFIG.equipment.lbMax, Math.max(0, Math.ceil((enh - CONFIG.equipment.levelCapBase) / CONFIG.equipment.levelCapPerLb)));
  const level = Math.min(enh, CONFIG.equipment.levelCapBase + CONFIG.equipment.levelCapPerLb * lb);
  p.items = [];
  for (const c of CHAR_IDS) {
    const mk = (defId: string, slot: 'weapon' | 'armor' | 'netsuke', charId: CharId | null) => {
      const id = p.nextId++;
      // 追加効果は1つ・中くらいの値（武器=攻撃、防具=HP、根付=防御）。affixValueMult は掛けない（推奨戦力を追加効果の調整で動かさない）
      const affixId: AffixId = slot === 'weapon' ? 'atk' : slot === 'armor' ? 'hp' : 'def';
      const m = AFFIX_POOL[affixId];
      const scale = CONFIG.equipment.meiTierScaleBase + CONFIG.equipment.meiTierScalePer * tier;
      const affixes = stageId >= s.meiFrom ? [{ id: affixId, value: Math.round(((m.lo + m.hi) / 2) * scale * 1000) / 1000, q: 0.5 }] : [];
      p.items.push({ id, defId, slot, charId, tier, rarity: stageId >= s.rareFrom ? 'r' : 'n', level, lb, affixes, rerolls: 0, rerollDay: '' });
      p.sets[c][slot] = id;
    };
    mk(`w_${c}_${tier}`, 'weapon', c);
    if (stageId >= s.armorFrom) mk(`a_${tier}`, 'armor', null);
    if (stageId >= s.netsukeFrom) mk(`n_${tier}`, 'netsuke', null);
  }
  const bp = Math.min(CONFIG.blueprint.maxLevel, Math.floor((stageId - 1) / s.bpDiv));
  for (const t of BUILD_ORDER) p.blueprints[t] = bp;
  // お守り：standard.omamori の [ステージ, 種類, 段] のうち、そのステージで当てはまる最後の1つ（枠は1つ）。段 s は品質 stageToQ(s)・副効果なし。
  // 枠はキャラごとなので、どのキャラでも同じ強さになるようキャラごとに同じ個体を作って付ける
  const om = s.omamori.filter(([from]) => stageId >= from).at(-1);
  if (om) {
    const [, defId, stage] = om;
    for (const c of CHAR_IDS) {
      const id = p.nextId++;
      p.omamori.push({ id, defId, rarity: 'r', q: stageToQ(stage), subs: [] });
      p.omamoriSets[c][0] = id;
    }
  }
  // 式神（10種族）：その章までの種族を shikiPerChapter の割合で持つ（章の若い順）。式神Lv は shikiLvPoints（進化もその Lv で）
  const lvAt = s.shikiLvPoints?.length ? polyline(s.shikiLvPoints, stageId) : 1 + s.shikiLvPerStage * (stageId - s.shikiLvFrom);
  const lv = Math.min(CONFIG.shikigami.maxLevel, Math.max(1, Math.round(lvAt)));
  const nShiki = Math.floor(SHIKIGAMI_IDS.filter((id) => SHIKIGAMI[id].chapter <= ch).length * s.shikiShare);
  for (const id of SHIKIGAMI_IDS.filter((id) => SHIKIGAMI[id].chapter <= ch).slice(0, nShiki)) p.shikigami[id] = { star: stageOf(lv), kills: 0, level: lv };
  // お供：持っている式神から4人の平均の戦力が最も高くなる1体（式神がいれば）
  // スキル：おすすめ構成（その主Lvで解放済みのものから解放力の範囲で autoBuild）。妖力の奥義は推奨戦力に入れない（腕前で早く勝てる要素。GAME_DESIGN 2.5）
  for (const c of CHAR_IDS) p.skills[c] = { set: autoBuild(p, c, undefined, OUGI_SKILL_IDS), auto: false, presets: [null, null], noOugi: true };
  if (stageId >= s.companionFrom) p.companion = recommendCompanion(p).enemyId;
  return p;
}

/**
 * 戦力が最も高くなるお供（charId を省くと4人の平均）。profile は変えない。仲間がいなければ enemyId は null。
 * 同じ式神・★・式神Lv は効果が同じなので、1回だけ評価する
 */
export function recommendCompanion(p: Profile, charId?: CharId): { enemyId: string | null; power: number } {
  const chars = charId ? [charId] : CHAR_IDS;
  const avg = (q: Profile) => chars.reduce((a, c) => a + rateOf(q, c), 0) / chars.length;
  const seen = new Set<string>();
  // 評価するキャラだけ、お供を差し替えた写しで戦力を測る
  const withComp = (id: string | null): Profile => ({ ...p, kits: { ...(p.kits ?? {}), ...Object.fromEntries(chars.map((c) => [c, { ...(p.kits?.[c] ?? { items: [...(p.battleItems ?? ['heal'])] }), companion: id }])) } });
  let best: { enemyId: string | null; power: number } = { enemyId: null, power: avg(withComp(null)) };
  const owned = Object.entries(p.shikigami ?? {})
    .filter(([id, st]) => st.star > 0 && SHIKIGAMI[id])
    .sort((a, b) => b[1].star - a[1].star || a[0].localeCompare(b[0]));
  for (const [id, st] of owned) {
    const key = `${id}:${st.star}:${st.level ?? 1}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const pw = avg(withComp(id));
    if (pw > best.power + 1e-9) best = { enemyId: id, power: pw };
  }
  return best;
}

/**
 * 想定育成の loadout。jutsu を渡すと、その術の系統（専用ツリーの列）を先に取ってから残りをおすすめで埋め、その術を持っていく
 * （術ごとの勝率をそろえるシミュレーター用。推奨戦力は基本の術のまま）
 */
export function standardLoadout(charId: CharId, stageId: number, jutsu?: string, weaponLine?: string): BattleLoadout {
  const p = standardProfile(stageId);
  // 較正用：武器の系統を差し替える（段・レアリティ・強化はそのまま）
  const w = weaponLine ? p.items.find((x) => x.id === p.sets[charId]?.weapon) : undefined;
  if (w) {
    const line = EQUIP_LINES.find((l) => l.id === weaponLine && l.charId === charId);
    if (line) w.defId = lineDefId(line, w.tier);
  }
  const j = jutsu ? JUTSU_BY_ID[jutsu] : undefined;
  if (j && j.charId === charId && j.kind !== 'strike') {
    const col = SKILLS.filter((s) => s.charId === charId && s.group === 'char' && s.col === j.col).sort((a, b) => a.tier - b.tier).map((s) => s.id);
    const cap = autoBuild(p, charId, undefined, OUGI_SKILL_IDS).reduce((a, id) => a + (SKILLS.find((s) => s.id === id)?.cost ?? 0), 0);
    const chosen: string[] = [];
    let used = 0;
    for (const id of col) {
      const s = SKILLS.find((x) => x.id === id)!;
      if (s.unlockLv > p.level || used + s.cost > cap) break;
      chosen.push(id);
      used += s.cost;
    }
    // 残りはおすすめの順で（列の術の強化を取ったぶん、ほかが減る）
    const rest = autoBuild(p, charId, cap - used, [...OUGI_SKILL_IDS, ...chosen]);
    p.skills[charId] = { set: [...chosen, ...rest], auto: false, presets: [null, null], jutsu: j.id, noOugi: true };
  }
  return buildLoadout(p, charId);
}

/** 推奨戦力の係数：1章は easyStart から1へ、その後 scaleRamp ステージかけて scale へ */
function recScale(stageId: number) {
  const s = CONFIG.standard;
  if (stageId <= s.scaleFrom) return s.easyStart + ((1 - s.easyStart) * (stageId - 1)) / Math.max(1, s.scaleFrom - 1);
  return 1 + (s.scale - 1) * Math.min(1, (stageId - s.scaleFrom) / s.scaleRamp);
}

let recCache = { ver: -1, map: new Map<string, number>() };

/** 推奨戦力。想定育成の4人の平均×章内の上乗せ（章ボスは更に×bossMult）×章ごとの倍率（recTune：ちょうどで勝率75%）×面ごとの倍率（battle/stage-tune.ts）×難易度 */
export function recommendedPower(stageId: number, difficulty = 0): number {
  if (recCache.ver !== configVersion()) recCache = { ver: configVersion(), map: new Map() };
  const key = `${stageId}:${difficulty}`;
  const hit = recCache.map.get(key);
  if (hit !== undefined) return hit;
  const s = CONFIG.standard;
  const p = standardProfile(stageId);
  const avg = CHAR_IDS.reduce((a, c) => a + powerOf(p, c), 0) / CHAR_IDS.length;
  const boss = isChapterBoss(stageId);
  const tune = (boss ? s.recTune.boss : s.recTune.normal)[chapterOf(stageId) - 1] ?? 1;
  const v = Math.round(
    avg * recScale(stageId) * (1 + s.inChapterSlope * (indexInChapter(stageId) - 1)) * (boss ? s.bossMult : 1) * tune * (STAGE_TUNE[stageId] ?? 1) * Math.pow(s.difficultyMult, difficulty),
  );
  recCache.map.set(key, v);
  return v;
}

/**
 * キャラごとの推奨戦力：推奨戦力 ×（想定育成でのそのキャラの戦力 ÷ 4人の平均）。同じ育成でもキャラで数字が違う
 * （HPの多いキャラ・術や射程のあるキャラは高く出る）。最小手番ランキングの上限など、キャラごとに比べるときに使う
 */
export function recommendedPowerFor(stageId: number, difficulty: number, charId: CharId): number {
  const p = standardProfile(stageId);
  const avg = CHAR_IDS.reduce((a, c) => a + powerOf(p, c), 0) / CHAR_IDS.length;
  return Math.round(recommendedPower(stageId, difficulty) * (powerOf(p, charId) / Math.max(1, avg)));
}

/** 最小手番ランキングに載る戦力の上限：キャラごとの推奨戦力と想定育成の戦力の高い方 × powerRatio（序盤は推奨戦力が想定育成より低く、育てていないキャラでも超えてしまうため） */
export function rankingPowerCap(stageId: number, difficulty: number, charId: CharId): number {
  const own = powerOf(standardProfile(stageId), charId) * Math.pow(CONFIG.standard.difficultyMult, difficulty);
  return Math.round(Math.max(recommendedPowerFor(stageId, difficulty, charId), own) * CONFIG.stageRanking.powerRatio);
}

/** 一番戦力の高いキャラ */
export function bestChar(profile: Profile): { charId: CharId; power: number } {
  let best = { charId: CHAR_IDS[0], power: -1 };
  for (const c of CHAR_IDS) {
    const pw = powerOf(profile, c);
    if (pw > best.power) best = { charId: c, power: pw };
  }
  return best;
}

// ---- おすすめ装備（戦力が最も高くなる組み合わせ） ----

export interface Recommendation {
  set: Record<'weapon' | 'armor' | 'netsuke', number | null>;
  before: number;
  after: number;
  /** 他のキャラから外して付け替える品の id */
  takenFrom: { itemId: number; charId: CharId }[];
}

/**
 * その装備で戦力が最も高くなる組み合わせを探す（profile は変えない）。
 * 3枠を1つずつ最良に入れ替えるのを、変化がなくなるまで繰り返す（追加効果の組み合わせも戦力計算で評価）。
 * includeOthers=false なら、他のキャラが付けている品は候補にしない。
 */
export function recommendEquipment(p: Profile, charId: CharId, includeOthers = false): Recommendation {
  const slots = ['weapon', 'armor', 'netsuke'] as const;
  const owner = new Map<number, CharId>();
  for (const c of CHAR_IDS) for (const s of slots) { const id = p.sets[c][s]; if (id != null && c !== charId) owner.set(id, c); }
  const work: Profile = { ...p, sets: { ...p.sets, [charId]: { ...p.sets[charId] } } };
  const before = powerOf(p, charId);
  let best = rateOf(p, charId);
  for (let pass = 0; pass < 4; pass++) {
    let changed = false;
    for (const s of slots) {
      const cands = p.items.filter((it) => it.slot === s && (s !== 'weapon' || it.charId === charId) && (includeOthers || !owner.has(it.id)));
      let pick = work.sets[charId][s];
      for (const it of cands) {
        if (it.id === pick) continue;
        const trial: Profile = { ...work, sets: { ...work.sets, [charId]: { ...work.sets[charId], [s]: it.id } } };
        const pw = rateOf(trial, charId);
        if (pw > best) { best = pw; pick = it.id; changed = true; }
      }
      work.sets[charId] = { ...work.sets[charId], [s]: pick };
    }
    if (!changed) break;
  }
  const set = { ...work.sets[charId] } as Recommendation['set'];
  const takenFrom = slots.map((s) => set[s]).filter((id): id is number => id != null && owner.has(id)).map((id) => ({ itemId: id, charId: owner.get(id)! }));
  return { set, before, after: powerOf({ ...p, sets: { ...p.sets, [charId]: set } } as Profile, charId), takenFrom };
}

/** おすすめ装備を実際に付ける（他キャラから取る品はそのキャラから外れる） */
export function applyRecommendedEquipment(p: Profile, charId: CharId, includeOthers = false): Recommendation {
  const r = recommendEquipment(p, charId, includeOthers);
  for (const s of ['weapon', 'armor', 'netsuke'] as const) {
    const id = r.set[s];
    if (id == null) p.sets[charId][s] = null;
    else {
      for (const c of CHAR_IDS) if (p.sets[c][s] === id) p.sets[c][s] = null;
      p.sets[charId][s] = id;
    }
  }
  return r;
}

const healCount = (items: { id: string; count: number }[]) => items.find((x) => x.id === 'heal')?.count ?? 0;
/** 基本の支給数より多い分（回復薬+n）。戦力の補正用。回復薬を選んでいなければ回復薬の数がまるごと上乗せ分 */
function itemExtra(lo: BattleLoadout): number {
  if (!lo.items) return lo.potions.count - POTION_BASE.count;
  const h = lo.items.find((x) => x.id === 'heal');
  if (!h) return 0;
  const picked = lo.items.length <= 2 && h.count >= ITEM_BASE_COUNT.heal;
  return picked ? h.count - ITEM_BASE_COUNT.heal : h.count;
}

/**
 * おすすめビルド（編成の「おすすめビルド」）：型（styles.ts）でそのキャラを組む。key='std' はおすすめ（標準）。
 * - スキル：型の優先リストで組む（以後も主Lvが上がるたびに型で組み直す）。std はおすすめ構成
 * - 装備：戦力が最も高い組み合わせ。型があれば、好みの追加効果を持つものを少し優先。includeOthers なら他のキャラが付けている品も外して使う
 * - 道具：型の2つ（万屋で解放済みのものだけ、足りなければ回復薬）。std は変えない
 * - 式神：型の候補で持っている先頭、無ければそのキャラで最も強くなる1体
 */
export function applyBuildStyle(p: Profile, charId: CharId, key = 'std', includeOthers = false): { before: number; after: number; style: string } {
  const before = powerOf(p, charId);
  const style = styleOf(charId, key);
  if (style) applyStyleSkills(p, charId, style.key);
  else applyAutoBuild(p, charId);
  // おすすめ（std）は戦力が最も高い組み合わせ（おすすめ装備と同じ）。型は好みの追加効果も見る
  if (style) applyStyleEquipment(p, charId, style.affix, includeOthers);
  else applyRecommendedEquipment(p, charId, includeOthers);
  if (style) {
    const owned = unlockedItems(p);
    const items = style.items.filter((id) => owned.includes(id));
    if (!items.includes('heal') && items.length < ITEM_SLOTS) items.unshift('heal');
    setKit(p, charId, { items: items.slice(0, ITEM_SLOTS) });
  }
  const comp = style?.comp.find((id) => (p.shikigami[id]?.star ?? 0) > 0) ?? recommendCompanion(p, charId).enemyId;
  if (comp) setKit(p, charId, { companion: comp });
  // お守り：型はその戦い方に合う種類を優先（戦力ではなく）。おすすめ（std）は戦力の最も上がる1つ
  const om = pickOmamori(p, charId, style?.omamori);
  if (om !== undefined) equipOmamori(p, charId, 0, om);
  return { before, after: powerOf(p, charId), style: style?.key ?? 'std' };
}

/**
 * 付けるお守り（1枠）。他のキャラが付けているものは使わない。kinds があれば、持っている中で先頭の種類のうち最も戦力の上がる1つ。
 * 無ければ（std・合う種類を持っていない）今のものも含めて戦力の最も高い1つ。変えないなら undefined
 */
export function pickOmamori(p: Profile, charId: CharId, kinds?: readonly string[]): number | null | undefined {
  const cur = p.omamoriSets[charId]?.[0] ?? null;
  const free = p.omamori.filter((o) => { const by = omamoriOwner(p, o.id); return !by || by === charId; });
  const powerWith = (id: number | null) => rateOf({ ...p, omamoriSets: { ...p.omamoriSets, [charId]: [id] } } as Profile, charId);
  const best = (list: typeof free) => {
    let pick: { id: number | null; pw: number } | null = null;
    for (const o of list) { const pw = powerWith(o.id); if (!pick || pw > pick.pw) pick = { id: o.id, pw }; }
    return pick;
  };
  // 上限で効かない種類（早建てを取ったスズネの二度建て守、吉の銘を付けたときの賽の目守など）は飛ばす
  const none = powerWith(null);
  for (const kind of kinds ?? []) {
    const hit = best(free.filter((o) => omamoriKind(o.defId) === kind));
    if (hit && hit.pw > none) return hit.id === cur ? undefined : hit.id;
  }
  const hit = best(free);
  if (!hit || hit.pw <= powerWith(cur)) return undefined;
  return hit.id;
}

/**
 * 装備：部位ごとに戦力が最も高くなるものを付ける。型の好みの追加効果は、戦力が最良とほぼ同じ（STYLE_EQUIP_SLACK 以内）ものの中で多い方を選ぶ
 * （以前は好み1つにつき戦力全体を4%高く見ていて、小さな好みの銘のために1段下の装備を選んでいた）。includeOthers なら他のキャラが付けている共用品も外して使う
 */
const STYLE_EQUIP_SLACK = 0.02;
function applyStyleEquipment(p: Profile, charId: CharId, affix: readonly string[] | null, includeOthers: boolean) {
  const slots = ['weapon', 'armor', 'netsuke'] as const;
  const worn = new Set(CHAR_IDS.filter((c) => c !== charId).flatMap((c) => slots.map((s) => p.sets[c][s])));
  const like = (it: { affixes?: { id: string }[] }) => (affix ? (it.affixes ?? []).filter((a) => affix.includes(a.id)).length : 0);
  for (const s of slots) {
    const cands: { id: number; pw: number; like: number }[] = [];
    for (const it of p.items) {
      if (it.slot !== s || (it.charId && it.charId !== charId)) continue;
      if (!includeOthers && worn.has(it.id)) continue;
      const def = EQUIP_DEFS[it.defId];
      if (def?.charId && def.charId !== charId) continue;
      const q = { ...p, sets: { ...p.sets, [charId]: { ...p.sets[charId], [s]: it.id } } } as Profile;
      cands.push({ id: it.id, pw: rateOf(q, charId), like: like(it) });
    }
    const top = Math.max(-Infinity, ...cands.map((c) => c.pw));
    const pick = cands.filter((c) => c.pw >= top * (1 - STYLE_EQUIP_SLACK)).sort((a, b) => b.like - a.like || b.pw - a.pw)[0];
    const bestId: number | null = pick?.id ?? p.sets[charId][s];
    if (bestId == null) continue;
    for (const c of CHAR_IDS) if (c !== charId && p.sets[c][s] === bestId) p.sets[c][s] = null;
    p.sets[charId][s] = bestId;
  }
}
